import React, { createContext, useState, useEffect, useContext } from 'react';
import { breakpoints } from '@/utils/util-breakpoints';

interface ResponsiveContextType {
  isXs: boolean; // < 576px
  isSm: boolean; // ≥ 576px
  isMd: boolean; // ≥ 768px
  isLg: boolean; // ≥ 992px
  isXl: boolean; // ≥ 1200px
  isTablet: boolean; // md or lg (≥ 768px and < 1200px)
  isMobile: boolean; // xs or sm (< 768px)
}

const ResponsiveContext = createContext<ResponsiveContextType | undefined>(undefined);

export const ResponsiveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  // Calculate responsive states based on window width
  const isXs = windowSize < breakpoints.sm;
  const isSm = windowSize >= breakpoints.sm && windowSize < breakpoints.md;
  const isMd = windowSize >= breakpoints.md && windowSize < breakpoints.lg;
  const isLg = windowSize >= breakpoints.lg && windowSize < breakpoints.xl;
  const isXl = windowSize >= breakpoints.xl;
  const isTablet = windowSize >= breakpoints.md && windowSize < breakpoints.xl;
  const isMobile = windowSize < breakpoints.md;

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ResponsiveContext.Provider value={{ isXs, isSm, isMd, isLg, isXl, isTablet, isMobile }}>
      {children}
    </ResponsiveContext.Provider>
  );
};

export const useResponsive = () => {
  const context = useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }
  return context;
};
